import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Check, ChevronDown, Search } from 'lucide-react';

// A dropdown you can type into: the list opens with a search box on top, so picking one person
// out of a long staff list is a few letters instead of a scroll. Works like a <select> for the
// page — `value` / `onChange(value)` — with an "All …" row for no filter.
//
//   <SearchSelect value={staffId} onChange={setStaffId} allLabel="All staff"
//     options={staff.map(s => ({ value: s.id, label: `${s.first_name} ${s.last_name}` }))} />
//
// Keyboard: ↑ ↓ move, Enter picks, Esc closes. The menu is portalled, so a card or table never
// clips it.
export default function SearchSelect({
  value, onChange, options = [], allLabel = 'All', placeholder = 'Type to search…', ariaLabel, className,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const listId = useId();

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const hits = q ? options.filter((o) => String(o.label).toLowerCase().includes(q)) : options;
    return q ? hits : [{ value: '', label: allLabel }, ...hits];
  }, [query, options, allLabel]);
  const current = options.find((o) => o.value === value);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.max(r.width, 260);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    const up = below < 240 && above > below;
    const maxH = Math.min(340, up ? above : below);
    setPos({ left, width, maxH, top: up ? undefined : r.bottom + 4, bottom: up ? window.innerHeight - r.top + 4 : undefined });
  };

  const openMenu = () => {
    place();
    setQuery('');
    const idx = options.findIndex((o) => o.value === value);
    setActive(idx >= 0 ? idx + 1 : 0);
    setOpen(true);
  };
  const close = (refocus = true) => { setOpen(false); if (refocus) btnRef.current?.focus(); };
  const choose = (v) => { onChange(v); close(); };

  useEffect(() => {
    if (!open) return undefined;
    inputRef.current?.focus();
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      close(false);
    };
    const onScroll = (e) => { if (!panelRef.current?.contains(e.target)) place(); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]); // eslint-disable-line

  // Typing moves the highlight to the first match.
  useEffect(() => { if (open) setActive(0); }, [query]); // eslint-disable-line

  useLayoutEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); const it = items[active]; if (it) choose(it.value); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
    else if (e.key === 'Tab') { close(false); }
  };

  return (
    <>
      <button ref={btnRef} type="button" aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={(e) => { if ((e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') && !open) { e.preventDefault(); openMenu(); } }}
        className={clsx('input-field !w-auto !py-1.5 text-[13px] inline-flex items-center justify-between gap-2 min-w-[150px] max-w-[220px] text-left',
          value && '!border-primary-300 bg-primary-50/40', className)}>
        <span className={clsx('truncate', !current && 'text-slate-700')}>{current ? current.label : allLabel}</span>
        <ChevronDown size={14} className={clsx('text-slate-400 shrink-0 transition-transform', open && 'rotate-180')} aria-hidden="true" />
      </button>
      {open && pos && createPortal(
        <div ref={panelRef}
          className="fixed z-[90] bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden flex flex-col"
          style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxH }}>
          <div className="p-2 border-b border-slate-100 shrink-0">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
              <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onKeyDown}
                role="combobox" aria-expanded="true" aria-controls={listId} aria-autocomplete="list"
                aria-activedescendant={items[active] ? `${listId}-${active}` : undefined}
                aria-label={ariaLabel ? `Search ${ariaLabel.toLowerCase()}` : 'Search'}
                placeholder={placeholder}
                className="w-full h-9 pl-8 pr-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100" />
            </div>
          </div>
          <ul id={listId} role="listbox" aria-label={ariaLabel} className="overflow-y-auto py-1">
            {items.length === 0 && <li className="px-3 py-3 text-sm text-slate-500">No match for “{query}”.</li>}
            {items.map((it, i) => {
              const selected = it.value === (value || '');
              return (
                <li key={`${it.value}-${i}`} id={`${listId}-${i}`} data-idx={i} role="option" aria-selected={selected}
                  onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); choose(it.value); }}
                  className={clsx('flex items-center gap-2 px-3 py-2 text-sm cursor-pointer',
                    i === active ? 'bg-primary-50 text-primary-900' : 'text-slate-700',
                    it.value === '' && 'font-medium')}>
                  <span className="flex-1 truncate">{it.label}</span>
                  {selected && <Check size={14} className="text-primary-600 shrink-0" aria-hidden="true" />}
                </li>
              );
            })}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}
